// S2-FE1: API fetch wrapper
// Base URL from env, API key auth header, typed responses

const API_BASE = import.meta.env.VITE_API_URL || '';
const API_KEY = import.meta.env.VITE_API_KEY || '';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`API ${status}: ${body}`);
    this.name = 'ApiError';
  }
}

/**
 * Typed fetch wrapper for evaluation API.
 * Automatically adds auth header and content-type.
 */
export async function apiFetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(API_KEY ? { Authorization: `Bearer ${API_KEY}` } : {}),
    ...(options?.headers as Record<string, string> || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.text().catch(() => 'Unknown error');
    throw new ApiError(res.status, body);
  }

  return res.json();
}

/**
 * POST with JSON body — convenience wrapper.
 */
export async function apiPost<T>(
  path: string,
  body: unknown,
): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Creates an SSE connection for POST endpoints that stream responses.
 * Used for long-running operations (evaluation runs, readiness checks).
 * Returns a ReadableStream reader — use with useSSE hook.
 */
export async function apiStream(
  path: string,
  body: unknown,
): Promise<ReadableStream<Uint8Array>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    ...(API_KEY ? { Authorization: `Bearer ${API_KEY}` } : {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error');
    throw new ApiError(res.status, text);
  }

  if (!res.body) {
    throw new ApiError(0, 'No response body for streaming');
  }

  return res.body;
}
